/**
 * Risk categories least to most severe; the index **is** the 0–4 level the rules emit.
 */
export const IMPACT_CATEGORIES = ['minimal', 'low', 'moderate', 'high', 'major'] as const

export type ImpactCategory = (typeof IMPACT_CATEGORIES)[number]

/** The 0–4 level a raw impact score falls in — what both conversions below are built on */
const impactToLevel = (impact: number | null | undefined): number =>
	Number.isFinite(impact) && (impact as number) >= 1 ? Math.min(IMPACT_CATEGORIES.length - 1, Math.ceil(impact as number)) : 0

export const impactToCategory = (impact: number | null | undefined): ImpactCategory =>
	IMPACT_CATEGORIES[impactToLevel(impact)] ?? IMPACT_CATEGORIES[0]

/** Bits per category count, capping a count at 31 — above the most slots an axis can hold (25) */
const COUNT_BITS = 5
const COUNT_BASE = 1 << COUNT_BITS
const COUNT_MAX = COUNT_BASE - 1

/**
 * Sort rank of a row: a histogram of its cells by category, packed so a plain numeric comparison
 * orders by the count of `major` cells first, then `high`, down to `minimal`. Three high hours
 * outrank one, while no number of `high` cells overtakes a single `major` — what packing buys over
 * a weighted sum.
 *
 * Five 5-bit counts fit in 25 bits, so the rank is an exact integer comparable by subtraction.
 * Counts saturate rather than carrying into the next category's bits, which would invert the
 * order — hence slot scores (25 at most) rather than a whole week of hours.
 */
export const severityRank = (scores: Iterable<number>): number => {
	const counts = new Uint8Array(IMPACT_CATEGORIES.length)

	for (const score of scores) {
		const level = impactToLevel(score)
		const count = counts[level] ?? 0
		if (count < COUNT_MAX) counts[level] = count + 1
	}

	// Most severe category last, so it ends up in the high bits
	return counts.reduceRight((rank, count) => rank * COUNT_BASE + count, 0)
}
